import { reactive } from 'vue';

import cloneDeep from 'lodash/cloneDeep';

import { ValidationError } from '@/exceptions';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ModalParamsType = Record<string | number, any>;

export type ModalOptionsType = {
  params?: ModalParamsType,
  disableDeepCopy?: boolean,
};

export type ModalsType = {
  [name: string]: {
    visible: boolean,
    params?: ModalParamsType,
  },
};

class ModalManager {
  modals: ModalsType = {};

  constructor() {
    this.modals = {};
  }

  register(name: string): void {
    if (name in this.modals) return;
    this.modals[name] = { visible: false, params: {} };
  }

  unregister(name: string): void {
    if (name in this.modals) {
      delete this.modals[name];
    }
  }

  isVisible(name: string): boolean {
    if (!(name in this.modals)) return false;
    return this.modals[name].visible;
  }

  show(
    name: string,
    options: ModalOptionsType = {
      params: {},
      disableDeepCopy: false,
    },
  ): void {
    if (!(name in this.modals)) {
      throw new ValidationError(`Not found: Failed to open modal "${name}".`);
    }
    Object.keys(this.modals).forEach((key) => {
      this.modals[key].visible = key === name;
      if (options?.params && !options.disableDeepCopy) {
        this.modals[key].params = cloneDeep(options.params);
      } else {
        this.modals[key].params = JSON.parse(JSON.stringify(options.params));
      }
    });
  }

  hide(name: string): void {
    if (!(name in this.modals)) {
      throw new ValidationError(`Not found: Failed to close modal "${name}".`);
    }
    this.modals[name].visible = false;
    this.modals[name].params = {};
  }

  getParams(name: string): ModalParamsType {
    if (!(name in this.modals)) {
      throw new ValidationError(`Not found: Failed to ge params of "${name}".`);
    }
    const { params } = this.modals[name];
    if (params !== null && params !== undefined) {
      return params;
    }
    return {};
  }
}

const modalManager = reactive(new ModalManager());

export const useModal = () => modalManager;
